
import { SimplePensionFormOfPayment } from 'app/calcs/pensions/interfaces';
import { Profile, updateLifeExpectancy } from 'app/redux/Profile/model'
import { Gender } from 'app/redux/Profile/model/interfaces'
import * as _ from 'lodash'
import * as moment from 'moment'

// define the names of all the columns (properties) in a profile's 'extraInfo'
const ColumnNames = {
    memberLastName: 'LAST NAME',
    memberFirstName: 'FIRST NAME',
    memberDOB: 'DOB',
    memberGender: 'SEX',
    spouseFirstName: 'SPOUSE FIRST NAME',
    spouseLastName: 'SPOUSE LAST NAME',
    spouseDOB: 'SPOUSE DOB',
    spouseGender: 'SPOUSE SEX',
    uBenefitService: 'UPS BENEFIT SERVICE',
    uAccruedBenefit: 'UPS BENEFIT',
    cyhBenefitService: 'C,Y,H BENEFIT SERVICE',
    cyhAccruedBenefit: 'C,Y,H BENEFIT',
    employerCode: 'EMPLOYER CODE',

    uBenefitCurrentEmpContrib: 'UPS BENEFIT CURRENT EMP CONTRIB',
    cCurrentEmpContrib: 'C CURRENT EMP CONTRIB',

    YRCHollandBenSvcTo2009: 'Y,H BENEFIT SERVICE THRU 2009',
    YRCVestSvcFrozenPd: 'Y,H VESTING SERVICE'
}

const genderMap = {
    M: Gender.male,
    F: Gender.female
}

const gm = (g: string) => genderMap[g] || Gender.unidentified

/**
 * Populates a profile with the information found in 'extraInfo'.
 *
 * @param profile - the profile to load.
 */
function loadProfilePresets(profile: Profile): Promise<Profile> {
    const ex = profile.extraInfo

    const member = profile.getHeadOfHousehold()
    member.firstName = ex[ColumnNames.memberFirstName]
    member.lastName = ex[ColumnNames.memberLastName]
    member.dob = moment.utc(ex[ColumnNames.memberDOB])
    member.gender = gm(ex[ColumnNames.memberGender])

    const sln = ex[ColumnNames.spouseLastName]
    if (sln) {
        const spouse = profile.getSpouse()
        spouse.firstName = ex[ColumnNames.spouseFirstName]
        spouse.lastName = sln
        spouse.dob = moment.utc(ex[ColumnNames.spouseDOB])
        spouse.gender = gm(ex[ColumnNames.spouseGender])
    }

    const beginPaymentsAge = moment.duration(65, 'years')
    const formOfPayment = SimplePensionFormOfPayment.FiftyPercentJointAndSurvivor
    const futureWeeksPerYear = 52

    profile.pension.local705 = {
        accruedBenefit: 0,
        beginPaymentsAge,
        benefitServiceYears: null,
        employerCode: null,
        formOfPayment,
        futureWeeksPerYear,
        employerContribution: 0,
        YRCHollandBenSvcTo2009: 0,
        YRCVestSvcFrozenPd: 0
    }

    loadUpgradeableData(profile)

    return updateLifeExpectancy(member)
    .then(() => {
        if (sln) {
            return updateLifeExpectancy(profile.getSpouse())
            .then(() => profile)
        }
        return profile
    })
}

function loadUpgradeableData(profile: Profile) {
    const ex = profile.extraInfo

    const benefitServiceYears = moment.duration(ex[ColumnNames.cyhBenefitService] + ex[ColumnNames.uBenefitService], 'years')
    const employerCode = ex[ColumnNames.employerCode]

    // in v2.0 spreadsheet
    const employerContribution = ex[ColumnNames.cCurrentEmpContrib] + ex[ColumnNames.uBenefitCurrentEmpContrib]
    const YRCHollandBenSvcTo2009 = ex[ColumnNames.YRCHollandBenSvcTo2009]
    const YRCVestSvcFrozenPd = ex[ColumnNames.YRCVestSvcFrozenPd]
    const accruedBenefit = ex[ColumnNames.cyhAccruedBenefit] + ex[ColumnNames.uAccruedBenefit]

    profile.pension.local705 = {
        ...profile.pension.local705,
        accruedBenefit,
        benefitServiceYears,
        employerCode,
        employerContribution,
        YRCHollandBenSvcTo2009,
        YRCVestSvcFrozenPd
    }
}

function migrate1p4To2Spreadsheet(profile: Profile) {
    const ex = profile.extraInfo
    const employerContribution = ex[ColumnNames.cCurrentEmpContrib] + ex[ColumnNames.uBenefitCurrentEmpContrib]
    const YRCHollandBenSvcTo2009 = ex[ColumnNames.YRCHollandBenSvcTo2009] || 0
    const YRCVestSvcFrozenPd = ex[ColumnNames.YRCVestSvcFrozenPd]
    _.merge(profile.pension.local705, { employerContribution, YRCHollandBenSvcTo2009, YRCVestSvcFrozenPd })
}

/**
 * Returns an array of the information in 'extraInfo' decribed by a column name.
 *
 * @param profile - the profile containing the 'extraInfo'
 * @param columnNames - a list of columns required
 */
function pickColumns(profile: Profile, columnNames: string[]): any[] {
    if (!profile.extraInfo) { return [] }
    return _.map(columnNames, (propName) => profile.extraInfo[propName])
}

export {
    ColumnNames,
    loadProfilePresets,
    migrate1p4To2Spreadsheet,
    pickColumns,
    loadUpgradeableData
}



// WEBPACK FOOTER //
// ./src/config/705/ETL/index.ts